<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 hide-on-print">
      <SimplePageHeader title="Invoicing" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="cloud_download"
            color="primary"
            text="Excel"
            class="hide-on-print"
            :block="true"
            @click="handleExportToExcel"
          />
          <ButtonPrint
            class="mt-3"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <InvoicingReportFilters
        class="px-5 mb-sm-5 pb-8"
      ></InvoicingReportFilters>
    </div>
    <SkippedAssignmentsWarning :skippedAssignments="skippedAssignments"></SkippedAssignmentsWarning>
    <ReportHeader
      reportTitle="Invoicing Report"
      :reportCriteria="reportFilterCriteria"
    ></ReportHeader>
    <v-row
      v-if="invoiceFrequencyId && clientBillingSummariesDataValue.length > 0"
      class="hide-on-print"
    >
      <v-col cols="12" class="pt-sm-0">
        <v-card class="mt-3 mt-md-0 pt-sm-0">
          <v-card-text>
            <v-row>
              <v-col cols="12" class="px-4">
                <span class="headline">
                  <small>
                    Proration Needed
                    <div class="yellow-square"></div>
                  </small>
                </span>
                <span class="headline ml-5">
                  <small>
                    Total Bill Edited
                    <div class="blue-square"></div>
                  </small>
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      class="my-0 py-0"
    >
      <v-col cols="12" class="py-0">
        <NoDataAvailableMessage
          v-if="invoiceFrequencyId && clientBillingSummariesDataValue.length === 0"
          message="No data for this time period"
        ></NoDataAvailableMessage>
        <InvoicingReportTable
          v-if="invoiceFrequencyId && clientBillingSummariesDataValue.length > 0"
          :clientBillingSummaries="clientBillingSummariesDataValue"
          :businessEntityId="businessEntityId"
          @OneOffFeeListModalClose="getInvoicingReportWithFilters"
        ></InvoicingReportTable>
      </v-col>
    </v-row>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { ButtonPrint, BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import ReportHeader from '@/components/reports/ReportHeader'
import InvoicingReportFilters from './InvoicingReportFilters'
import { SimplePageHeader } from '@/components/pageComponents'
import { mapFields } from '@/vueHelpers/vuex'
import InvoicingReportTable from '@/components/invoicingReport/InvoicingReportTable'
import { mapActions } from 'vuex'
import excel from '@/lib/export/excel'
import { getInvoicingFrequencyLabel, getBillRateTypeLabel, formatDate, formatCurrency, formatDecimal } from '@/lib/display/index'
import { getPresentTypeSummaries, getTypeSummaryHours, getTypeSummaryAmount, TypeSummaryAmountField, isTypePresentOnRow, buildSkippedRow } from '@/lib/reports'
import { NoDataAvailableMessage, SkippedAssignmentsWarning } from '@/components/reports'
import { setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'
import { ALL_BUSINESS_ENTITIES } from '@/lib/constants/businessEntities'

const INVOICING_REPORT_QUERY_KEYS = ['invoiceFrequencyId', 'dateBegin', 'dateEnd', 'businessEntityId', 'showDetails', 'clientId']

export default {
  components: {
    ButtonPrint,
    BottomSpacer,
    ReportHeader,
    InvoicingReportFilters,
    SimplePageHeader,
    ButtonCustomIcon,
    InvoicingReportTable,
    NoDataAvailableMessage,
    SkippedAssignmentsWarning
  },
  data () {
    return {
      billingSummariesEmpty: [],
      isInitialized: false
    }
  },
  computed: {
    ...mapFields('invoicingReport', ['invoicingReport']),
    ...mapFields('invoicingReportFilters', [
      'invoiceFrequencyId',
      'dateBegin',
      'dateEnd',
      'businessEntityId',
      'clientId',
      'showDetails'
    ]),
    clientBillingSummariesDataValue: {
      get () {
        if (this.invoicingReport &&
          this.invoicingReport.clientBillingSummaries &&
          this.invoicingReport.clientBillingSummaries.length > 0) {
          if (this.clientId) {
            return this.invoicingReport.clientBillingSummaries.filter(row => row.clientId === this.clientId)
          } else {
            return this.invoicingReport.clientBillingSummaries
          }
        }
        return this.billingSummariesEmpty
      }
    },
    reportFilterCriteria: {
      get () {
        return [
          { 'key': 'Invoice Frequency', 'value': getInvoicingFrequencyLabel(this.invoiceFrequencyId) },
          { 'key': 'Period', 'value': formatDate(this.dateBegin) + ' - ' + formatDate(this.dateEnd) }
        ]
      }
    },
    skippedAssignments: {
      get () {
        return (this.invoicingReport && this.invoicingReport.skippedAssignments) || []
      }
    }
  },
  watch: {
    invoiceFrequencyId: 'handleReportFilterChange',
    dateBegin: 'handleReportFilterChange',
    dateEnd: 'handleReportFilterChange',
    showDetails: 'handleReportFilterChange',
    businessEntityId: 'handleReportFilterChange',
    clientId: 'handleReportFilterChange',
    '$route.query' (nextQuery) {
      // Filter changes update the URL first. Once the route watcher runs, the store filters and
      // route query already match, so skip reapplying the filters and fetching the report twice.
      if (!this.isInitialized || this.queryDescribesCurrentFilters(nextQuery)) {
        return
      }

      // A query that does not match the current filters came from outside this component, such as
      // browser navigation or a bookmarked URL, so apply it and load the corresponding report.
      this.applyFiltersFromQuery(nextQuery)
      this.fetchReportFromQuery()
    }
  },
  methods: {
    ...mapActions('invoicingReport', ['getInvoicingReportWithFilters', 'clearInvoicingReport']),
    ...mapActions('invoicingReportFilters', ['clearInvoicingReportFilters']),
    parsePositiveInteger (value) {
      const parsed = Number(value)
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null
    },
    // businessEntityId defaults to the ALL_BUSINESS_ENTITIES sentinel (-1), not null, so a
    // missing or unparseable query value must fall back to that sentinel rather than to null.
    parseBusinessEntityId (value) {
      const parsed = Number(value)
      if (parsed === ALL_BUSINESS_ENTITIES) {
        return ALL_BUSINESS_ENTITIES
      }
      return Number.isInteger(parsed) && parsed > 0 ? parsed : ALL_BUSINESS_ENTITIES
    },
    parseQueryString (value) {
      return typeof value === 'string' && value.length > 0 ? value : null
    },
    applyFiltersFromQuery (query = this.$route.query) {
      const invoiceFrequencyId = this.parsePositiveInteger(query.invoiceFrequencyId)
      const businessEntityId = this.parseBusinessEntityId(query.businessEntityId)
      const clientId = this.parsePositiveInteger(query.clientId)
      const dateBegin = this.parseQueryString(query.dateBegin)
      const dateEnd = this.parseQueryString(query.dateEnd)

      this.invoiceFrequencyId = invoiceFrequencyId
      this.businessEntityId = businessEntityId
      this.dateBegin = dateBegin
      this.dateEnd = dateEnd
      this.showDetails = query.showDetails === 'true'
      this.clientId = clientId
    },
    buildReportFiltersQuery () {
      const nextQuery = { ...this.$route.query }

      setOrDeleteQueryParam(nextQuery, 'invoiceFrequencyId', this.invoiceFrequencyId)
      setOrDeleteQueryParam(nextQuery, 'dateBegin', this.dateBegin)
      setOrDeleteQueryParam(nextQuery, 'dateEnd', this.dateEnd)
      setOrDeleteQueryParam(nextQuery, 'showDetails', this.showDetails ? 'true' : null)
      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.businessEntityId)
      setOrDeleteQueryParam(nextQuery, 'clientId', this.clientId)

      return nextQuery
    },
    syncReportFiltersToQuery () {
      const nextQuery = this.buildReportFiltersQuery()

      if (hasQueryChanged(this.$route.query, nextQuery, INVOICING_REPORT_QUERY_KEYS)) {
        this.$router.replace({ query: nextQuery })
      }
    },
    queryDescribesCurrentFilters (query) {
      // Build the query represented by the current filter state. buildReportFiltersQuery preserves
      // unrelated route parameters, while this comparison only considers keys owned by this report.
      const nextQuery = this.buildReportFiltersQuery()

      return INVOICING_REPORT_QUERY_KEYS.every(key => query[key] === nextQuery[key])
    },
    handleReportFilterChange () {
      if (this.isInitialized) {
        this.syncReportFiltersToQuery()
      }
    },
    fetchReportFromQuery () {
      if (this.invoiceFrequencyId && this.dateBegin && this.dateEnd) {
        this.getInvoicingReportWithFilters()
      }
    },
    handleExportToExcel () {
      const allSummaries = []
      for (let client of this.clientBillingSummariesDataValue) {
        allSummaries.push(...client.billingSummaries)
      }
      const skippedAssignments = this.skippedAssignments
      if (allSummaries.length === 0 && skippedAssignments.length === 0) {
        return
      }

      const types = getPresentTypeSummaries(allSummaries)

      const data = allSummaries.map(assignment => {
        const row = {
          invoiceFrequency: getInvoicingFrequencyLabel(assignment.invoiceFrequencyId),
          periodBilled: formatDate(this.dateBegin) + ' - ' + formatDate(this.dateEnd),
          consultant: assignment.userFullName,
          client: assignment.clientName,
          billRate: formatCurrency(assignment.billRate),
          billRateType: getBillRateTypeLabel(assignment.billRateTypeId),
          totalBillableHours: parseFloat(assignment.totalBillableHours).toFixed(2),
          totalBill: formatCurrency(assignment.totalBill),
          frozenTotal: assignment.totalBillEdited === true ? formatCurrency(assignment.totalBill) : '',
          computedTotal: assignment.totalBillEdited === true ? formatCurrency(assignment.computedTotalBill) : '',
          status: ''
        }
        types.forEach(type => {
          const present = isTypePresentOnRow(assignment, type.timeTypeId)
          row[`type_${type.timeTypeId}_hours`] = present ? formatDecimal(getTypeSummaryHours(assignment, type.timeTypeId)) : 'N/A'
          row[`type_${type.timeTypeId}_amount`] = present ? formatCurrency(getTypeSummaryAmount(assignment, type.timeTypeId, TypeSummaryAmountField.BILL)) : 'N/A'
        })
        return row
      })

      const skippedColumnKeys = {
        frequency: 'invoiceFrequency',
        period: 'periodBilled',
        rate: 'billRate',
        rateType: 'billRateType',
        totalHours: 'totalBillableHours',
        total: 'totalBill'
      }
      const skippedSharedValues = {
        frequency: getInvoicingFrequencyLabel(this.invoiceFrequencyId),
        period: formatDate(this.dateBegin) + ' - ' + formatDate(this.dateEnd)
      }
      skippedAssignments.forEach(skipped => {
        data.push(buildSkippedRow(skipped, types, skippedColumnKeys, skippedSharedValues))
      })

      let title = ['Invoice Frequency', 'Period Billed', 'Client', 'Consultant', 'Standard Rate', 'Bill Rate Type']
      let key = ['invoiceFrequency', 'periodBilled', 'client', 'consultant', 'billRate', 'billRateType']
      if (this.showDetails) {
        types.forEach(type => {
          title.push(type.timeTypeName, `${type.timeTypeName} Bill`)
          key.push(`type_${type.timeTypeId}_hours`, `type_${type.timeTypeId}_amount`)
        })
      }
      title.push('Total Billable Hours', 'Total Bill', 'Frozen Total', 'Computed Total', 'Status')
      key.push('totalBillableHours', 'totalBill', 'frozenTotal', 'computedTotal', 'status')

      const params = {
        title: title,
        key: key,
        data: data,
        autoWidth: true,
        filename: `Invoicing`
      }
      excel.exportArray2Excel(params)
    }
  },
  created () {
    this.clearInvoicingReport()
    this.clearInvoicingReportFilters()
    this.applyFiltersFromQuery()
    this.isInitialized = true
    this.fetchReportFromQuery()
  }
}
</script>
